import React, { useEffect } from 'react';
import {
  Hero as SharingHero,
  Storefront,
} from '@spotify-internal/landing-page-library';
import Hero from '../Hero';
import Faq from '../Faq';
import { ErrorHero } from '../Shared/ErrorHero';
import { MastheadResponse } from '../../server/proto/generated-prototools/grpc/com/spotify/wwwmastheadapi';
import {
  HeroType,
  BenefitsType,
  StorefrontType,
  FaqType,
  ErrorType,
  SharingHeroType,
} from '../../server/types';
import Benefits from '../Benefits';
import I18nManager from '../I18nManager';
import '@spotify-internal/landing-page-library/esm/styles/masthead/index.css';
import { usePublicRuntimeConfigValue } from '../../lib/config';
import { track } from '@spotify-internal/track-js';
import { TextDirection } from '../../server/api/country-attributes/country-attributes';
import { Header, Footer } from '@spotify-internal/masthead-react';
import '@spotify-internal/masthead-react/index.css';

type TrackJsConfig = {
  enabled: boolean;
  googleTagManagerId?: string;
};

export type SchedulerData = {
  metadata: {
    title: string;
    description: string;
    image: string;
  };
  components: Array<
    | FaqType
    | BenefitsType
    | StorefrontType
    | HeroType
    | ErrorType
    | SharingHeroType
  >;
};

export type BasePageProps = {
  masthead?: MastheadResponse;
  direction: TextDirection;
  language: string;
  market: string;
  oneTrust: boolean;
  translationLocales?: string[];
};

export function BasePage({
  basePageProps: { masthead, language, direction, market, oneTrust },
  schedulerData,
}: {
  basePageProps: BasePageProps;
  schedulerData: SchedulerData;
}) {
  const { enabled: trackJsEnabled, googleTagManagerId } =
    usePublicRuntimeConfigValue<TrackJsConfig>('analytics.trackJs');

  useEffect(() => {
    if (!trackJsEnabled) {
      return;
    }

    // To minimize page flicker, we manually install
    // Google Optimize outside of our app bundle. See
    // _document.tsx.
    track({
      market,
      language,
      googleTagManagerId,
      oneTrust,
    });
  }, [oneTrust, googleTagManagerId, language, market, trackJsEnabled]);

  return (
    <I18nManager language={language.replace('_', '-')} direction={direction}>
      {masthead?.header && (
        <Header
          fixed={false}
          transparent={false}
          {...(masthead.header as React.ComponentPropsWithoutRef<
            typeof Header
          >)}
        />
      )}
      <main>
        {!!schedulerData.components &&
          schedulerData.components.map((component, i) => {
            switch (component.name) {
              case 'faq':
                return <Faq {...component.attributes} key={`faq-${i}`} />;
              case 'benefits':
                return (
                  <Benefits {...component.attributes} key={`benefits-${i}`} />
                );
              case 'storefront':
                return (
                  <Storefront
                    {...component.attributes}
                    key={`storefront-${i}`}
                  />
                );
              case 'error':
                return (
                  <ErrorHero {...component.attributes} key={`error-${i}`} />
                );
              case 'hero':
                return <Hero {...component.attributes} key={`hero-${i}`} />;
              case 'sharing-hero':
                return (
                  <SharingHero
                    {...component.attributes}
                    key={`sharing-hero-${i}`}
                  />
                );
              default:
                return <></>;
            }
          })}
      </main>
      {masthead?.footer && (
        <Footer
          {...(masthead.footer as React.ComponentPropsWithoutRef<
            typeof Footer
          >)}
        />
      )}
    </I18nManager>
  );
}

export default BasePage;
